import axios from 'axios';
import { BASE_URL, BEARER_TOKEN } from '../config/environment';
import { startRequest, endRequest } from '../utils/loadingBar';

const SESSION_TOKEN_KEY = 'avsight_session_token';

// Session token management (localStorage-based to avoid third-party cookie issues)
export const getSessionToken = () => localStorage.getItem(SESSION_TOKEN_KEY);
export const setSessionToken = (token) => localStorage.setItem(SESSION_TOKEN_KEY, token);
export const clearSessionToken = () => localStorage.removeItem(SESSION_TOKEN_KEY);

// Configure axios defaults
const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${BEARER_TOKEN}`,
    'X-Portal-Domain': window.location.hostname, // Send domain for multi-tenant resolution
  },
});

// Add session token to every request if available
apiClient.interceptors.request.use((config) => {
  const sessionToken = getSessionToken();
  if (sessionToken) {
    config.headers['X-Session-Token'] = sessionToken;
  }
  return config;
});

// Drive the global top-of-page progress bar off real request activity, so it
// covers both page-switch data loads and in-page fetches (search, sort, etc.)
// without every caller having to manage its own loading UI.
apiClient.interceptors.request.use((config) => {
  if (!config.skipLoadingBar) startRequest();
  return config;
});
apiClient.interceptors.response.use(
  (response) => {
    if (!response.config?.skipLoadingBar) endRequest();
    return response;
  },
  (error) => {
    if (!error.config?.skipLoadingBar) endRequest();
    return Promise.reject(error);
  }
);

// Auth flow API methods
export const validateLogin = () => {
  return apiClient.get('/validate-login');
};

// Legacy auth methods (kept for backwards compatibility)
export const verifyEmail = (email, url) => {
  return apiClient.post('/verify', { email, url });
};

export const updatePreferences = (data) => {
  return apiClient.post('/preferences/update', data);
};

export const sendCode = (email) => {
  return apiClient.post('/code/send', { email });
};

export const verifyCode = (email, code) => {
  return apiClient.post('/code/verify', { email, code });
};

// New PIN-based auth flow methods
export const checkEmail = (email) => {
  return apiClient.post('/auth/check', { email });
};

export const register = (email, name, company, phone, jobTitle, partSales, mro) => {
  return apiClient.post('/auth/register', { email, name, company, phone, jobTitle, partSales, mro });
};

export const sendPin = (email) => {
  return apiClient.post('/auth/send', { email });
};

export const validatePin = (email, pin) => {
  return apiClient.post('/auth/validate', { email, pin });
};

export const getUserDetails = () => {
  return apiClient.get('/user/details');
};

export const getCaseList = () => {
  return apiClient.get('/case/getlist');
};

export const logout = () => {
  return apiClient.post('/auth/logout');
};

export const deleteAccount = () => {
  return apiClient.delete('/user/account');
};

// Health check endpoints
export const getHealth = () => {
  return apiClient.get('/health');
};

export const getTenantHealth = () => {
  return apiClient.get('/health/tenant');
};

export const getTenantDeepHealth = () => {
  return apiClient.get('/health/tenant/deep');
};

// Portal status endpoint (public - no auth/credentials required)
// Uses separate axios instance without credentials to work with Azure CORS wildcard
export const getPortalStatus = () => {
  return axios.get(`${BASE_URL}portal-status`, {
    headers: {
      'Content-Type': 'application/json',
      'X-Portal-Domain': window.location.hostname,
    },
  });
};

export default apiClient;

